'use client';
import { iRequestContext } from '@/app/models/contextModels/requestContext';
import styles from './contactformblock.module.scss';
import { logError } from '@/app/helpers/azureBlobLogger';
import { iContactFormBlock } from '@/app/models/blockTypes/contactFormBlock';
import { useEffect, useState } from 'react';
import Loader from '@/app/components/Loader/Loader';
import { iContactFormMailModel } from '@/app/models/contactFormMailModel';
import { iDictionary } from '@/app/models/dictionary';
import FormInput from '@/app/components/Forms/FormInput';
import LinkElement from '@/app/components/General/LinkElement';
import { eButtonColorTheme } from '@/app/models/enum/eButtonColorTheme';
import { eChevronPosition } from '@/app/models/enum/chevronPosition';
import { clientLogError, clientLogInfo } from '@/app/helpers/clientLoggingHelper';
import FormCheckbox from '@/app/components/Forms/FormCheckbox';
import { eLinkSize } from '@/app/models/enum/linkSize';
import Modal from '@/app/components/Modal/Modal';
import ClientBlockList from '../ClientBlockList/ClientBlockList';
import { iPolicyContent } from '@/app/models/policyContent';
import FormSelect from '@/app/components/Forms/FormSelect';
import { iSelectItem } from '@/app/models/componentModels/selectItem';

interface Props {
    block: iContactFormBlock;
    requestContext: iRequestContext;
    policyContent: iPolicyContent | undefined;

}


export default function ContactFormBlock({ block, requestContext, policyContent }: Props) {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [formModel, setFormModel] = useState<iContactFormMailModel>();
    const [showError, setShowError] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [marketingChecked, setMarketingChecked] = useState<boolean>(false);
    const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState<boolean>(false);
    const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] = useState(false);
    const [availableCategories, setAvailableCategories] = useState<iSelectItem[]>([]);
    const [showValidationError, setShowValidationError] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<iDictionary>();
    const [isValid, setIsValid] = useState<boolean>(false);

    const [forenameDirty, setForenameDirty] = useState<boolean>(false);
    const [surnameDirty, setSurnameDirty] = useState<boolean>(false);
    const [emailDirty, setEmailDirty] = useState<boolean>(false);
    const [phoneDirty, setPhoneDirty] = useState<boolean>(false);
    const [categoryDirty, setCategoryDirty] = useState<boolean>(false);
    const [messageDirty, setMessageDirty] = useState<boolean>(false);

    useEffect(() => {
        setAvailableCategories([
            { value: 'invoice', label: requestContext.translations.blocks.contactFormBlock.categories.invoice },
            { value: 'accessLock', label: requestContext.translations.blocks.contactFormBlock.categories.accessLock },
            { value: 'accessCode', label: requestContext.translations.blocks.contactFormBlock.categories.accessCode },
            { value: 'termination', label: requestContext.translations.blocks.contactFormBlock.categories.termination },
            { value: 'other', label: requestContext.translations.blocks.contactFormBlock.categories.other },
        ]);
    }, []);

    useEffect(() => {
        validateFormModel();
    }, [formModel]);

    useEffect(() => {
        validateFormModel();
    }, [privacyPolicyChecked]);

    const updateValue = (name: string, value: string) => {
        const newModel = { ...formModel, [name]: value };
        setFormModel(newModel);
        setIsDirty(true);
    }

    const validateFormModel = (): boolean => {
        let isValidTemp = true;
        let newInputErrors: iDictionary = {};

        if ((!formModel?.forename || formModel?.forename.length === 0) && forenameDirty) {
            isValidTemp = false;
            newInputErrors = {
                ...newInputErrors, ["forename"]: [`${requestContext.translations.general.forename} ${requestContext.translations.general.form.isMandatory}`]
            };
        }

        if ((!formModel?.surname || formModel?.surname.length === 0) && surnameDirty) {
            isValidTemp = false;
            newInputErrors = {
                ...newInputErrors, ["surname"]: [`${requestContext.translations.general.surname} ${requestContext.translations.general.form.isMandatory}`]
            };
        }

        if (emailDirty) {
            if (!formModel?.mail || formModel?.mail.length === 0) {
                isValidTemp = false;
                newInputErrors = {
                    ...newInputErrors, ["mail"]: [`${requestContext.translations.general.email} ${requestContext.translations.general.form.isMandatory}`]
                };
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formModel?.mail) && formModel?.mail.length !== 0) {
                if (emailDirty) {
                    isValidTemp = false;
                    newInputErrors = {
                        ...newInputErrors, ["mail"]: [`${requestContext.translations.general.form.emailValidError}`]
                    };
                }
            }
        }

        if ((!formModel?.phone || formModel?.phone.length === 0) && phoneDirty) {
            isValidTemp = false;
            newInputErrors = {
                ...newInputErrors, ["phone"]: [`${requestContext.translations.general.phoneNumber} ${requestContext.translations.general.form.isMandatory}`]
            };
        }

        if ((!formModel?.category || formModel?.category.length === 0) && categoryDirty) {
            isValidTemp = false;
            newInputErrors = {
                ...newInputErrors, ["category"]: [`${requestContext.translations.blocks.contactFormBlock.chooseCategory} ${requestContext.translations.general.form.isMandatory}`]
            };
        }

        if ((!formModel?.message || formModel?.message.length === 0) && messageDirty) {
            isValidTemp = false;
            newInputErrors = {
                ...newInputErrors, ["message"]: [`${requestContext.translations.blocks.contactFormBlock.yourMessage} ${requestContext.translations.general.form.isMandatory}`]
            };
        }

        // if (!formModel?.acceptTerms) {
        //     isValidTemp = false;
        //     newInputErrors = {
        //         ...newInputErrors, ["acceptTerms"]: [`${requestContext.translations.blocks.contactFormBlock.privacyPolicy} ${requestContext.translations.general.form.isMandatory}`]
        //     };
        // }

        setValidationErrors(newInputErrors);
        setIsValid(isValidTemp);
        setIsDirty(forenameDirty && surnameDirty && emailDirty && phoneDirty && categoryDirty && messageDirty);
        return isValidTemp;
    }

    const submitForm = async () => {
        setIsLoading(true);
        setIsDirty(true);

        if (!validateFormModel()) {
            setIsLoading(false);
            // setShowError(true);
            setShowValidationError(true)
            setShowSuccess(false);
            return;
        } else {
            setShowValidationError(false);
        }

        try {
            if (formModel) {
                formModel.receivingMail = block.properties.receivingMail;
                const orgName = requestContext.configurations.temporaryOrgName ? requestContext.configurations.temporaryOrgName : requestContext.translations.general.organization;
                formModel.subject = `${orgName} ${requestContext.translations.blocks.contactFormBlock.subject} ${formModel.category}`;
                formModel.forenameSubject = requestContext.translations.general.forename;
                formModel.surnameSubject = requestContext.translations.general.surname;
                formModel.mailSubject = requestContext.translations.general.email;
                formModel.phoneSubject = requestContext.translations.general.phoneNumber;
                formModel.categorySubject = requestContext.translations.blocks.contactFormBlock.category;
                formModel.messageSubject = requestContext.translations.blocks.contactFormBlock.message;
                formModel.acceptTermsSubject = requestContext.translations.blocks.contactFormBlock.privacyPolicySubject;
                formModel.acceptMarketingSubject = requestContext.translations.blocks.contactFormBlock.marketingTextSubject;
                formModel.acceptMarketingText = formModel.acceptMarketing ? requestContext.translations.general.yes : requestContext.translations.general.no;
                formModel.acceptTermsText = formModel.acceptTerms ? requestContext.translations.general.yes : requestContext.translations.general.no;
            }

            clientLogInfo("ContactFormBlock.tsx", "submitForm", "formModel", formModel);

            const response = await fetch("/api/user/sendContactForm", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Country: requestContext.country,
                },
                body: JSON.stringify(formModel)
            });

            if (!response.ok) {
                clientLogError("ContactFormBlock.tsx", "submitForm", "response not ok!", await response.text());
                throw new Error("Logout failed");
            }

            const success = await response.json() as boolean;

            if (success) {
                clientLogInfo("ContactFormBlock.tsx", "submitForm", "contact form sent successfully");
                setShowSuccess(true);
                setShowError(false);
                setIsDirty(false);
            } else {
                setShowSuccess(false);
                setShowError(true);
            }
        } catch (error) {
            setShowError(true);
            setShowSuccess(false);
            // Handle error
            clientLogError("ContactFormBlock.tsx", "submitForm", error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleClosePrivacyPolicyModal = () => {
        setIsPrivacyPolicyModalOpen(false);
    }

    const onCategoryChange = (name: string, value: string | boolean, altValue?: any) => {
        setFormModel({
            ...formModel, [name]: altValue ? altValue : value
        });
    };

    try {
        return (
            <div className="p-6 xl:p-0">
                <div className={`w-full GetBlockWrapperClasses(block)`} >
                    {isLoading ? (
                        <div className="mb-6">
                            <Loader
                                loading={isLoading}
                                useFullWindow={false}
                                useNoBackground={true}
                            />
                        </div>
                    ) : (
                        <>
                            {showValidationError && (
                                <div className="mb-10 text-center">
                                    <h3 className="font-warning">{requestContext.translations.blocks.contactFormBlock.errors.validation.title}</h3>
                                    <div>{requestContext.translations.blocks.contactFormBlock.errors.validation.message}</div>
                                </div>
                            )}
                            {showError && (
                                <div className="mb-10 text-center">
                                    <h3 className="font-warning">{requestContext.translations.blocks.contactFormBlock.errors.error.title}</h3>
                                    <div>{requestContext.translations.blocks.contactFormBlock.errors.error.message}</div>
                                </div>
                            )}
                            {showSuccess ? (
                                <div className="mb-10 text-center">
                                    <h3>{requestContext.translations.blocks.contactFormBlock.successMessage}</h3>
                                </div>
                            ) : (<>
                                <div className="grid grid-cols-2 mb-10 gap-4 w-full lg:w-2/3">
                                    <FormInput
                                        name="forename"
                                        label={requestContext.translations.general.forename}
                                        placeholder={requestContext.translations.general.forename}
                                        type="text"
                                        isMandatory={true}
                                        onValueChange={(name, value) => {
                                            updateValue(name, value)
                                            setForenameDirty(true);
                                        }}
                                        errors={validationErrors}
                                        value={formModel?.forename ?? ""}
                                    />

                                    <FormInput
                                        name="surname"
                                        label={requestContext.translations.general.surname}
                                        placeholder={requestContext.translations.general.surname}
                                        type="text"
                                        isMandatory={true}
                                        onValueChange={(name, value) => {
                                            updateValue(name, value);
                                            setSurnameDirty(true);
                                        }}
                                        errors={validationErrors}
                                        value={formModel?.surname ?? ""}
                                    />

                                    <FormInput
                                        name="mail"
                                        label={requestContext.translations.general.email}
                                        placeholder={requestContext.translations.general.email}
                                        type="text"
                                        isMandatory={true}
                                        onValueChange={(name, value) => {
                                            updateValue(name, value);
                                            setEmailDirty(true);
                                        }}
                                        errors={validationErrors}
                                        value={formModel?.mail ?? ""}
                                    />

                                    <FormInput
                                        name="phone"
                                        label={requestContext.translations.general.phoneNumber}
                                        placeholder={requestContext.translations.general.phoneNumber}
                                        type="text"
                                        isMandatory={true}
                                        onValueChange={(name, value) => {
                                            updateValue(name, value);
                                            setPhoneDirty(true);
                                        }}
                                        errors={validationErrors}
                                        value={formModel?.phone ?? ""}
                                    />

                                    <FormSelect
                                        name="category"
                                        label={
                                            requestContext.translations.blocks.contactFormBlock.chooseCategory
                                        }
                                        errors={validationErrors}
                                        options={availableCategories}
                                        placeholder={requestContext.translations.blocks.contactFormBlock.chooseCategory}
                                        useEmptyDefault={true}
                                        className="mb-6 col-span-2"
                                        isMandatory={true}
                                        onValueChange={(name, value) => {
                                            const categoryText = availableCategories.find(item => item.value === value)?.label;
                                            onCategoryChange(name, value, categoryText);
                                            setCategoryDirty(true);
                                        }}
                                        value={availableCategories.find(item => item.label === formModel?.category)?.value}
                                    />

                                    <FormInput
                                        name="message"
                                        label={requestContext.translations.blocks.contactFormBlock.yourMessage}
                                        placeholder={requestContext.translations.blocks.contactFormBlock.yourMessage}
                                        type="textarea"
                                        isMandatory={true}
                                        className="col-span-2"
                                        innerClassName="h-[200px]"
                                        onValueChange={(name, value) => {
                                            updateValue(name, value);
                                            setMessageDirty(true);
                                        }}
                                        errors={validationErrors}
                                        value={formModel?.message ?? ""}
                                    />

                                    <FormCheckbox
                                        name="acceptTerms"
                                        checked={privacyPolicyChecked}
                                        onChange={(e) => {
                                            setFormModel({
                                                ...formModel,
                                                acceptTerms: e.target.checked
                                            });

                                            setPrivacyPolicyChecked(e.target.checked);

                                        }}
                                        errors={validationErrors}
                                        className="col-span-2"
                                    >
                                        {requestContext.translations.blocks.contactFormBlock.privacyPolicyText}
                                        <LinkElement
                                            title={requestContext.translations.blocks.contactFormBlock.privacyPolicyLinkText}
                                            className="ml-1"
                                            linkSize={eLinkSize.Large}
                                            onClick={() => setIsPrivacyPolicyModalOpen(true)}
                                        >
                                            {requestContext.translations.blocks.contactFormBlock.privacyPolicyLinkText}
                                        </LinkElement>.*
                                    </FormCheckbox>

                                    <FormCheckbox
                                        name="acceptMarketing"
                                        checked={marketingChecked}
                                        onChange={(e) => {
                                            setMarketingChecked(e.target.checked);
                                            setFormModel({
                                                ...formModel,
                                                acceptMarketing: e.target.checked
                                            });
                                        }}
                                        errors={validationErrors}
                                        className="col-span-2"
                                    >
                                        {requestContext.translations.blocks.contactFormBlock.marketingText}
                                    </FormCheckbox>


                                    {showValidationError && (
                                        <div className="mb-10 text-center col-span-2 ">
                                            <h3 className="font-warning">{requestContext.translations.blocks.contactFormBlock.errors.validation.title}</h3>
                                            <div>{requestContext.translations.blocks.contactFormBlock.errors.validation.message}</div>
                                        </div>
                                    )}
                                    {showError && (
                                        <div className="mb-10 text-center col-span-2">
                                            <h3 className="font-warning">{requestContext.translations.blocks.contactFormBlock.errors.error.title}</h3>
                                            <div>{requestContext.translations.blocks.contactFormBlock.errors.error.message}</div>
                                        </div>
                                    )}

                                    <LinkElement
                                        title="skicka"
                                        isButton={true}
                                        buttonColorTheme={eButtonColorTheme.Green}
                                        onClick={submitForm}
                                        chevronPosition={eChevronPosition.None}
                                        className="col-span-2"
                                        disabled={!privacyPolicyChecked || !isDirty || !isValid || (validationErrors && Object.keys(validationErrors).length > 0)}
                                    >
                                        {requestContext.translations.blocks.contactFormBlock.send}
                                    </LinkElement>
                                </div >
                            </>)
                            }
                        </>
                    )}
                    {policyContent?.privacyPolicy && (
                        <Modal
                            isOpen={isPrivacyPolicyModalOpen}
                            onClose={handleClosePrivacyPolicyModal}
                            closeTranslation={requestContext.translations.general.closePopup}
                            className={`container pt-8 pl-4 pr-1 pb-4 lg:pt-12 lg:pl-10 lg:pr-10 lg:pb-10 ${styles.modal}`}
                        >
                            <div className={`${styles.modalContentContainer}`}>
                                <div className={`${styles.modalContent}`}>
                                    {policyContent?.privacyPolicy?.properties?.title && (
                                        <h2>{policyContent?.privacyPolicy?.properties.title}</h2>
                                    )}
                                    {policyContent?.privacyPolicy?.properties?.preamble && (
                                        <div>
                                            {policyContent?.privacyPolicy?.properties.preamble}
                                        </div>
                                    )}
                                    {policyContent?.privacyPolicy?.properties?.mainBody && (
                                        <div dangerouslySetInnerHTML={{ __html: policyContent?.privacyPolicy?.properties?.mainBody.markup }}></div>
                                    )}
                                    {policyContent?.privacyPolicy?.properties?.mainBlockArea && (
                                        <div className="px-0">
                                            <ClientBlockList blockListItems={policyContent?.privacyPolicy?.properties?.mainBlockArea.items} requestContext={requestContext} previewMode={false} className={styles.noPaddingOnBlocks} isCompact={true} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Modal>
                    )}
                </div>
            </div>
        );
    } catch (error) {
        logError("NewsBlock.tsx", 'Error when generating ContactFormBlock', 'Error', error);
    }
}
